import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PlusCircle, Edit2, Trash2, Eye, EyeOff, LogOut, Mail, FileText, ArrowLeft, Video, ExternalLink, Bold, Italic, List, ListOrdered, Heading2, Heading3, Link2, Undo2, Redo2 } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link_ext from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { title: '', category: 'Technology', excerpt: '', content: '', image_url: '', author: 'Seva Gases Team', published: true };
const CATEGORIES = ['Technology', 'Industry Insights', 'Safety & Compliance', 'Company News', 'Gas Applications'];

/* ── Rich Text Editor (TipTap) ── */
function RichEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link_ext.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[#CC2222] underline' } }),
      Placeholder.configure({ placeholder: 'Write your blog post here… Use the toolbar to format text.' }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  const addLink = useCallback(() => {
    const url = window.prompt('Enter URL');
    if (url) editor?.chain().focus().setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  const ToolBtn = ({ onClick, active, title, children }) => (
    <button type="button" onClick={onClick} title={title}
      className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${active ? 'bg-[#CC2222] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
      {children}
    </button>
  );

  return (
    <div className="border border-gray-200 rounded-sm" data-testid="post-content-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-3 py-2 bg-gray-50 border-b border-gray-200">
        <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><Bold size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><Italic size={14} /></ToolBtn>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2"><Heading2 size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3"><Heading3 size={14} /></ToolBtn>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list"><List size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list"><ListOrdered size={14} /></ToolBtn>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <ToolBtn onClick={addLink} active={editor.isActive('link')} title="Add link"><Link2 size={14} /></ToolBtn>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Undo"><Undo2 size={14} /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Redo"><Redo2 size={14} /></ToolBtn>
      </div>
      {/* Editable area */}
      <EditorContent editor={editor}
        className="min-h-[280px] px-4 py-3 text-sm text-gray-800 focus:outline-none prose prose-sm max-w-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[260px] [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none" />
    </div>
  );
}

// ---- LOGIN SCREEN ----
function LoginScreen({ onLogin }) {
  const [creds, setCreds] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, creds);
      onLogin(res.data.token, res.data.username);
    } catch {
      setError('Invalid username or password');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-brand-red mx-auto mb-4 flex items-center justify-center">
            <FileText size={22} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white font-heading">Admin Panel</h1>
          <p className="text-gray-500 text-sm mt-1">Seva Gases Content Management</p>
        </div>
        <form onSubmit={handleLogin} data-testid="admin-login-form" className="bg-[#202020] border border-gray-800 p-8 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Username</label>
            <input value={creds.username} onChange={e => setCreds(c => ({ ...c, username: e.target.value }))}
              data-testid="admin-username-input"
              className="w-full bg-gray-800 border border-gray-700 text-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red" placeholder="admin" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Password</label>
            <input type="password" value={creds.password} onChange={e => setCreds(c => ({ ...c, password: e.target.value }))}
              data-testid="admin-password-input"
              className="w-full bg-gray-800 border border-gray-700 text-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red" placeholder="••••••••" />
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-btn"
            className="w-full bg-brand-red text-white py-3 font-semibold text-sm hover:bg-brand-red-dark transition-colors disabled:opacity-60">
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
        <p className="text-center mt-6">
          <Link to="/" className="text-gray-600 text-xs hover:text-gray-400 transition-colors flex items-center justify-center gap-1">
            <ArrowLeft size={12} /> Back to Website
          </Link>
        </p>
      </div>
    </div>
  );
}

// ---- POST FORM ----
function PostForm({ post, onSave, onCancel, token }) {
  const [form, setForm] = useState(post || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (post?.id) {
        await axios.put(`${API}/blog/${post.id}`, form, { headers });
      } else {
        await axios.post(`${API}/blog`, form, { headers });
      }
      onSave();
    } catch {
      setError('Failed to save. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-gray-200 p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 font-heading">{post?.id ? 'Edit Post' : 'Create New Post'}</h2>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 text-sm">Cancel</button>
      </div>
      <form onSubmit={handleSave} data-testid="post-form" className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              data-testid="post-title-input" required className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Category *</label>
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              data-testid="post-category-select" className="w-full border border-gray-200 px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-red">
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Author</label>
          <input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))}
            data-testid="post-author-input" className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Excerpt *</label>
          <textarea value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
            rows={2} data-testid="post-excerpt-input" required className="w-full border border-gray-200 px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-red" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
            Content * <span className="normal-case font-normal text-gray-400 ml-1">— use the toolbar for bold, headings, bullet points and links</span>
          </label>
          <RichEditor value={form.content} onChange={val => setForm(f => ({ ...f, content: val }))} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Image URL</label>
          <input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
            data-testid="post-image-input" placeholder="https://..." className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red" />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="published" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))}
            data-testid="post-published-checkbox" className="accent-brand-red" />
          <label htmlFor="published" className="text-sm text-gray-700">Published (visible on website)</label>
        </div>
        {error && <p className="text-red-500 text-sm">{error}</p>}
        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving} data-testid="post-save-btn"
            className="bg-brand-red text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-red-dark disabled:opacity-60">
            {saving ? 'Saving...' : post?.id ? 'Update Post' : 'Create Post'}
          </button>
          <button type="button" onClick={onCancel} className="border border-gray-300 px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
        </div>
      </form>
    </div>
  );
}

// ---- MARKETING VIDEO PANEL ----
function MarketingVideoPanel({ token }) {
  const [videoUrl, setVideoUrl] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/marketing/video`)
      .then(r => { setVideoUrl(r.data.video_url); setNewUrl(r.data.video_url); })
      .catch(() => {});
  }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess(false);
    try {
      await axios.put(`${API}/marketing/video`, { video_url: newUrl }, { headers: { Authorization: `Bearer ${token}` } });
      setVideoUrl(newUrl);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError('Failed to update. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-900 font-heading">Marketing Video</h2>
      <p className="text-gray-500 text-sm">This video plays as a floating popup on the homepage to promote products and services. Update the URL whenever you want to change the video.</p>

      <div className="bg-white border border-gray-200 p-6 space-y-5">
        {/* Current Video Preview */}
        {videoUrl && (
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Current Video</p>
            <div className="relative bg-black rounded overflow-hidden w-full max-w-sm">
              <video src={videoUrl} controls className="w-full aspect-video" data-testid="admin-video-preview" />
            </div>
            <a href={videoUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-xs text-brand-red hover:underline">
              <ExternalLink size={11} /> Open video URL
            </a>
          </div>
        )}

        {/* Update Form */}
        <form onSubmit={handleUpdate} data-testid="marketing-video-form" className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
              Video URL *
            </label>
            <input
              type="url"
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              required
              data-testid="marketing-video-url-input"
              placeholder="https://..."
              className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red"
            />
            <p className="mt-1 text-xs text-gray-400">Paste a direct video URL (.mp4 format recommended). This can be a link from cloud storage, CDN, or video hosting.</p>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          {success && <p className="text-green-600 text-sm font-semibold">Video URL updated successfully!</p>}
          <button
            type="submit"
            disabled={saving || newUrl === videoUrl}
            data-testid="marketing-video-save-btn"
            className="bg-brand-red text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-red-dark disabled:opacity-60 flex items-center gap-2"
          >
            <Video size={14} />
            {saving ? 'Updating...' : 'Update Video'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---- MAIN ADMIN ----
export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem('seva_admin_token') || '');
  const [adminUser, setAdminUser] = useState(() => localStorage.getItem('seva_admin_user') || '');
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('posts');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);

  const onLogin = (t, u) => {
    localStorage.setItem('seva_admin_token', t);
    localStorage.setItem('seva_admin_user', u);
    setToken(t); setAdminUser(u);
  };
  const onLogout = () => {
    localStorage.removeItem('seva_admin_token');
    localStorage.removeItem('seva_admin_user');
    setToken(''); setAdminUser('');
  };

  const headers = { Authorization: `Bearer ${token}` };

  const loadPosts = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/blog/all`, { headers }); setPosts(r.data); }
    catch { if (axios.isAxiosError && arguments[0]?.response?.status === 401) onLogout(); }
    finally { setLoading(false); }
  };

  const loadMessages = async () => {
    try { const r = await axios.get(`${API}/contact/messages`, { headers }); setMessages(r.data); }
    catch {}
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (token) { loadPosts(); loadMessages(); } }, [token]);

  const deletePost = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await axios.delete(`${API}/blog/${id}`, { headers });
    loadPosts();
  };

  const formatDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; } };

  if (!token) return <LoginScreen onLogin={onLogin} />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ADMIN NAV */}
      <nav className="bg-[#202020] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 bg-brand-red flex items-center justify-center">
              <FileText size={16} className="text-white" />
            </div>
            <span className="text-white font-bold font-heading">Seva Gases Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 text-sm hover:text-white transition-colors flex items-center gap-1">
              <ArrowLeft size={14} /> Website
            </Link>
            <span className="text-gray-500 text-sm">{adminUser}</span>
            <button onClick={onLogout} data-testid="admin-logout-btn"
              className="text-gray-400 hover:text-white flex items-center gap-1.5 text-sm">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* TABS */}
        <div className="flex gap-0 border-b border-gray-200 mb-8">
          {[{ key: 'posts', label: `Blog Posts (${posts.length})`, icon: FileText }, { key: 'messages', label: `Contact Messages (${messages.length})`, icon: Mail }, { key: 'marketing', label: 'Marketing Video', icon: Video }].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`admin-tab-${tab.key}`}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === tab.key ? 'border-brand-red text-brand-red' : 'border-transparent text-gray-600 hover:text-gray-900'}`}>
              <tab.icon size={15} />{tab.label}
            </button>
          ))}
        </div>

        {/* POSTS TAB */}
        {activeTab === 'posts' && (
          <div className="space-y-6">
            {creating ? (
              <PostForm token={token} onSave={() => { setCreating(false); loadPosts(); }} onCancel={() => setCreating(false)} />
            ) : editing ? (
              <PostForm token={token} post={editing} onSave={() => { setEditing(null); loadPosts(); }} onCancel={() => setEditing(null)} />
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900 font-heading">Blog Posts</h2>
                  <button onClick={() => setCreating(true)} data-testid="admin-create-post-btn"
                    className="bg-brand-red text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-red-dark flex items-center gap-2">
                    <PlusCircle size={16} /> New Post
                  </button>
                </div>
                {loading ? (
                  <div className="text-center py-12 text-gray-500">Loading...</div>
                ) : (
                  <div className="bg-white border border-gray-200 divide-y divide-gray-100">
                    {posts.length === 0 ? (
                      <p className="text-center py-12 text-gray-500">No posts yet. Create your first one!</p>
                    ) : posts.map(post => (
                      <div key={post.id} className="flex items-start gap-4 p-5 hover:bg-gray-50" data-testid={`admin-post-${post.id}`}>
                        {post.image_url && <img src={post.image_url} alt="" className="w-16 h-12 object-cover flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-semibold text-gray-900 text-sm truncate font-heading">{post.title}</h3>
                            <span className={`flex-shrink-0 text-xs px-2 py-0.5 font-medium ${post.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {post.published ? 'Published' : 'Draft'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-400">
                            <span className="text-brand-red">{post.category}</span>
                            <span>{formatDate(post.created_at)}</span>
                            <span>{post.author}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer"
                            className="w-8 h-8 border border-gray-200 flex items-center justify-center text-gray-500 hover:text-brand-red hover:border-brand-red transition-colors">
                            <Eye size={14} />
                          </a>
                          <button onClick={() => setEditing(post)} data-testid={`edit-post-${post.id}`}
                            className="w-8 h-8 border border-gray-200 flex items-center justify-center text-gray-500 hover:text-brand-red hover:border-brand-red transition-colors">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => deletePost(post.id)} data-testid={`delete-post-${post.id}`}
                            className="w-8 h-8 border border-gray-200 flex items-center justify-center text-gray-500 hover:text-red-500 hover:border-red-300 transition-colors">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* MESSAGES TAB */}
        {activeTab === 'messages' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900 font-heading">Contact Messages</h2>
            {messages.length === 0 ? (
              <div className="bg-white border border-gray-200 p-12 text-center text-gray-500">No messages yet.</div>
            ) : (
              <div className="bg-white border border-gray-200 divide-y divide-gray-100">
                {messages.map((msg, i) => (
                  <div key={msg.id} className="p-5" data-testid={`admin-message-${i}`}>
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <span className="font-semibold text-gray-900 text-sm font-heading">{msg.name}</span>
                        <span className="mx-2 text-gray-300">·</span>
                        <span className="text-brand-red text-xs font-semibold uppercase">{msg.subject}</span>
                      </div>
                      <span className="text-gray-400 text-xs flex-shrink-0">{formatDate(msg.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                      <a href={`mailto:${msg.email}`} className="hover:text-brand-red">{msg.email}</a>
                      <a href={`tel:${msg.phone}`} className="hover:text-brand-red">{msg.phone}</a>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed bg-gray-50 p-3 border-l-2 border-brand-red">{msg.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MARKETING TAB */}
        {activeTab === 'marketing' && <MarketingVideoPanel token={token} />}

      </div>
    </div>
  );
}
